import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import type {
  BalanceSeries,
  BalanceTimelineResponse,
  LedgerTapeResponse,
  MemberStatsResponse,
  RoundHistoryResponse,
} from '@tv-casino/shared';
import {
  fetchBalanceTimeline,
  fetchMemberHistory,
  fetchMemberLedger,
  fetchMemberStats,
  fetchMembers,
  grantChips,
  type CasinoMemberRow,
} from '../lib/casinoApi.js';
import { MemberMarket } from './MemberMarket.js';
import { ProfileBadge } from './ProfileBadge.js';
import { memberMarketModel } from '@tv-casino/viewmodels/components/memberMarketModel.js';
import {
  deskMemberOf,
  grantAmountOf,
  memberTableModel,
} from '@tv-casino/viewmodels/components/memberTableModel.js';
import { usePoll } from '../lib/usePoll.js';
import styles from './MemberTable.module.css';

/** Rounds / ledger page size; "load more" widens the ask, never past 200. */
const PAGE = 20;
const LEDGER_PAGE = 40;
/** The list and the open desk re-read while the tab is open, so the night
 * stays live even though stats ride REST rather than the room sockets. */
export const POLL_MS = 5000;

/**
 * The owner's member table: everyone who has walked into this casino, with
 * their balance, the night's result, a per-member market desk (quote,
 * blotter, ledger tape), and the grant/revoke controls.
 */
export function MemberTable({ casinoCode }: { casinoCode: string }) {
  const [members, setMembers] = useState<CasinoMemberRow[] | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [stats, setStats] = useState<MemberStatsResponse | null>(null);
  const [history, setHistory] = useState<RoundHistoryResponse | null>(null);
  const [ledger, setLedger] = useState<LedgerTapeResponse | null>(null);
  const [series, setSeries] = useState<BalanceSeries | null>(null);
  const [tradeLimit, setTradeLimit] = useState(PAGE);
  const [ledgerLimit, setLedgerLimit] = useState(LEDGER_PAGE);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [ledgerBusy, setLedgerBusy] = useState(false);
  const [amount, setAmount] = useState('100');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setMembers(await fetchMembers(casinoCode));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read the members.');
    }
  }, [casinoCode]);

  useEffect(() => {
    setMembers(null);
    setExpandedId(null);
    setHistory(null);
    setLedger(null);
    setSeries(null);
    void refresh();
  }, [refresh]);

  /** The desk open right now, so a poll never lands on a collapsed row. */
  const expandedRef = useRef<string | null>(null);
  useEffect(() => {
    expandedRef.current = expandedId;
  }, [expandedId]);

  /** Quiet list re-read: a missed poll keeps the page exactly as it was. */
  const pollMembers = useCallback(async () => {
    try {
      setMembers(await fetchMembers(casinoCode));
    } catch {
      // The next tick tries again; the last good list stays up.
    }
  }, [casinoCode]);

  /**
   * Quiet desk re-read at the page sizes already on screen, so balances,
   * counters, trades and the sparkline move as the tables settle. Results
   * for a member whose desk has since closed (or changed) are dropped.
   */
  const pollDesk = useCallback(
    async (memberId: string) => {
      const [statsRes, historyRes, ledgerRes, timelineRes] = await Promise.allSettled([
        fetchMemberStats(casinoCode, memberId),
        fetchMemberHistory(casinoCode, memberId, tradeLimit),
        fetchMemberLedger(casinoCode, memberId, ledgerLimit),
        fetchBalanceTimeline(casinoCode),
      ]);
      if (expandedRef.current !== memberId) return;
      if (statsRes.status === 'fulfilled') setStats(statsRes.value);
      if (historyRes.status === 'fulfilled') setHistory(historyRes.value);
      if (ledgerRes.status === 'fulfilled') setLedger(ledgerRes.value);
      if (timelineRes.status === 'fulfilled') {
        setSeries(timelineRes.value.series.find((s) => s.memberId === memberId) ?? null);
      }
    },
    [casinoCode, tradeLimit, ledgerLimit],
  );

  usePoll(() => {
    void pollMembers();
    if (expandedId) void pollDesk(expandedId);
  }, POLL_MS);

  const readHistory = useCallback(
    async (memberId: string, asked: number) => {
      setHistoryBusy(true);
      try {
        setHistory(await fetchMemberHistory(casinoCode, memberId, asked));
        setTradeLimit(asked);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not read that history.');
      } finally {
        setHistoryBusy(false);
      }
    },
    [casinoCode],
  );

  const readLedger = useCallback(
    async (memberId: string, asked: number) => {
      setLedgerBusy(true);
      try {
        setLedger(await fetchMemberLedger(casinoCode, memberId, asked));
        setLedgerLimit(asked);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not read that ledger.');
      } finally {
        setLedgerBusy(false);
      }
    },
    [casinoCode],
  );

  const expand = async (memberId: string) => {
    if (expandedId === memberId) {
      setExpandedId(null);
      setStats(null);
      setHistory(null);
      setLedger(null);
      setSeries(null);
      return;
    }
    setExpandedId(memberId);
    setStats(null);
    setHistory(null);
    setLedger(null);
    setSeries(null);
    setTradeLimit(PAGE);
    setLedgerLimit(LEDGER_PAGE);
    void readHistory(memberId, PAGE);
    void readLedger(memberId, LEDGER_PAGE);
    void fetchBalanceTimeline(casinoCode)
      .then((t: BalanceTimelineResponse) => {
        setSeries(t.series.find((s) => s.memberId === memberId) ?? null);
      })
      .catch(() => setSeries(null));
    try {
      setStats(await fetchMemberStats(casinoCode, memberId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read those stats.');
    }
  };

  const adjust = async (memberId: string, sign: 1 | -1) => {
    const parsed = grantAmountOf(amount);
    if (parsed === null) {
      setError('Enter a nonzero amount first.');
      return;
    }
    setBusy(true);
    try {
      await grantChips(casinoCode, memberId, sign * parsed);
      setError(null);
      await refresh();
      if (expandedId === memberId) {
        void readLedger(memberId, ledgerLimit);
        void fetchBalanceTimeline(casinoCode)
          .then((t) => setSeries(t.series.find((s) => s.memberId === memberId) ?? null))
          .catch(() => undefined);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The grant was refused.');
    } finally {
      setBusy(false);
    }
  };

  const vm = useMemo(
    () => memberTableModel(members, { expandedId, stats }),
    [members, expandedId, stats],
  );

  const marketMe = useMemo(
    () => deskMemberOf(members, expandedId, stats),
    [expandedId, stats, members],
  );

  const marketVm = useMemo(
    () =>
      memberMarketModel({
        me: marketMe,
        series,
        rounds: history?.entries ?? [],
        roundsHasMore: history?.hasMore ?? false,
        ledger: ledger?.entries ?? [],
        ledgerHasMore: ledger?.hasMore ?? false,
      }),
    [marketMe, series, history, ledger],
  );

  return (
    <section className={styles.panel} aria-label="Casino members">
      <div className={styles.head}>
        <p className={styles.title}>Members</p>
        <p className={styles.meta}>{vm.totalChipsLabel}</p>
      </div>

      {error && <p className={styles.error}>{error}</p>}

      {vm.emptyLine ? (
        <p className={styles.dim}>{vm.emptyLine}</p>
      ) : (
        <>
          <ul className={styles.rows}>
            {vm.rows.map((row) => (
              <li key={row.memberId} className={styles.row}>
                <div className={styles.rowHead}>
                  <span className={styles.name}>
                    {row.accentHex && (
                      <span
                        className={styles.dot}
                        style={{ '--accent': row.accentHex } as CSSProperties}
                        aria-hidden="true"
                      />
                    )}
                    {row.name}
                    {row.hasProfile && <ProfileBadge />}
                  </span>
                  <span className={styles.chips}>{row.chipsLabel}</span>
                </div>
                <p className={styles.meta}>
                  {row.metaLine} ·{' '}
                  <span
                    className={
                      row.netTone === 'win'
                        ? styles.win
                        : row.netTone === 'loss'
                          ? styles.loss
                          : undefined
                    }
                  >
                    {row.netLabel}
                  </span>
                </p>
                {row.driftWarning && <p className={styles.drift}>{row.driftWarning}</p>}

                <div className={styles.actions}>
                  <button type="button" onClick={() => void expand(row.memberId)}>
                    {row.expanded ? 'Hide desk' : 'Desk'}
                  </button>
                  <input
                    className={styles.amount}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    inputMode="numeric"
                    aria-label="Chip amount"
                  />
                  <button type="button" disabled={busy} onClick={() => void adjust(row.memberId, 1)}>
                    Grant
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void adjust(row.memberId, -1)}
                  >
                    Revoke
                  </button>
                </div>

                {row.expanded && (
                  <>
                    {marketVm.ready ? (
                      <MemberMarket
                        vm={marketVm}
                        compact
                        tradesBusy={historyBusy}
                        ledgerBusy={ledgerBusy}
                        onLoadMoreTrades={() => {
                          if (expandedId !== null) {
                            void readHistory(expandedId, Math.min(tradeLimit + PAGE, 200));
                          }
                        }}
                        onLoadMoreLedger={() => {
                          if (expandedId !== null) {
                            void readLedger(expandedId, Math.min(ledgerLimit + LEDGER_PAGE, 200));
                          }
                        }}
                      />
                    ) : (
                      <p className={styles.dim}>Reading the desk…</p>
                    )}

                    {row.statLines.length > 0 && (
                      <dl className={styles.statGrid}>
                        {row.statLines.map((line) => (
                          <div key={line.label}>
                            <dt className={styles.statLabel}>{line.label}</dt>
                            <dd className={styles.statValue}>{line.value}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
          <p className={styles.meta}>{vm.totalNetLabel}</p>
        </>
      )}
    </section>
  );
}
